import type { paths } from "@/api-schema";
import { PathsV1UserSearchPostParametersQueryExtend } from "@/api-schema-enums";
import { useUserSearchQuery } from "@/common/api/user/use-user-search.query";
import { UserTable } from "@/components/data-table/predefined/user-table";
import { userTableDefaultColumns } from "@/components/data-table/predefined/user-table-columns";
import { DataTableHeightMode, TableBackground } from "@/components/data-table/types";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ReactElement, ReactNode } from "react";
import { useMemo } from "react";
import { type Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { formSchemaType } from "./rule-form-fields";

type UserSearchBody = paths["/v1/user/search"]["post"]["requestBody"]["content"]["application/json"];

export const RULE_USER_PREVIEW_EXTEND = [
  PathsV1UserSearchPostParametersQueryExtend.Branches,
  PathsV1UserSearchPostParametersQueryExtend.Departments,
  PathsV1UserSearchPostParametersQueryExtend.Occupations,
  PathsV1UserSearchPostParametersQueryExtend.MainBranch,
];

const USER_PREVIEW_COLUMNS = [
  userTableDefaultColumns.columnAvatar,
  {
    ...userTableDefaultColumns.columnName,
    enableSorting: false,
  },
];

export function useRuleUserPreview(formControl: Control<formSchemaType>): {
  hasAnySelection: boolean;
  userPreviewBody: UserSearchBody;
  affectedUsersCount: number;
} {
  const [
    watchedCompanies,
    watchedCompanyGroups,
    watchedBranches,
    watchedDepartments,
    watchedOccupations,
    watchedBrands,
    watchedJobFunctions,
  ] = useWatch({
    control: formControl,
    name: ["companies", "companyGroups", "branches", "departments", "occupations", "brands", "jobFunctions"],
  });

  const hasAnySelection = useMemo(() => {
    return Boolean(
      watchedCompanies?.length ||
      watchedCompanyGroups?.length ||
      watchedBranches?.length ||
      watchedDepartments?.length ||
      watchedOccupations?.length ||
      watchedBrands?.length ||
      watchedJobFunctions?.length,
    );
  }, [
    watchedCompanies,
    watchedCompanyGroups,
    watchedBranches,
    watchedDepartments,
    watchedOccupations,
    watchedBrands,
    watchedJobFunctions,
  ]);

  const userPreviewBody = useMemo(
    () => ({
      ...(watchedCompanies?.length && { companies: watchedCompanies }),
      ...(watchedCompanyGroups?.length && { companyGroups: watchedCompanyGroups }),
      ...(watchedBranches?.length && { branches: watchedBranches }),
      ...(watchedDepartments?.length && { departments: watchedDepartments }),
      ...(watchedOccupations?.length && { occupations: watchedOccupations }),
      ...(watchedBrands?.length && { brands: watchedBrands }),
      ...(watchedJobFunctions?.length && { jobFunctions: watchedJobFunctions }),
    }),
    [
      watchedCompanies,
      watchedCompanyGroups,
      watchedBranches,
      watchedDepartments,
      watchedOccupations,
      watchedBrands,
      watchedJobFunctions,
    ],
  );

  const userPreviewCountBody = useMemo<UserSearchBody>(
    () => (hasAnySelection ? userPreviewBody : {}),
    [hasAnySelection, userPreviewBody],
  );

  const userPreviewCountQuery = useUserSearchQuery({
    options: {
      parameters: {
        pageSize: 1,
      },
      body: userPreviewCountBody,
    },
    enabled: hasAnySelection,
  });

  return {
    affectedUsersCount: hasAnySelection ? (userPreviewCountQuery.data?.meta?.total ?? 0) : 0,
    hasAnySelection,
    userPreviewBody,
  };
}

export function RuleAffectedUsersPreview({
  hasAnySelection,
  userPreviewBody,
}: {
  hasAnySelection: boolean;
  userPreviewBody: UserSearchBody;
}): ReactElement {
  const { t } = useTranslation(["ram"]);

  return (
    <div className="h-full">
      {hasAnySelection ? (
        <UserTable
          tableBackground={TableBackground.Card}
          body={userPreviewBody}
          query={{
            extend: RULE_USER_PREVIEW_EXTEND,
          }}
          columns={USER_PREVIEW_COLUMNS}
          features={{
            multiSelection: false,
            pagination: true,
            search: true,
            expansion: false,
          }}
          subComponent={undefined}
          pageSize={25}
          heightMode={DataTableHeightMode.Custom}
          showCreateButton={false}
          customRowActions={null}
        />
      ) : (
        <div className="text-muted-foreground rounded-lg border border-dashed py-8 text-center">
          {t("ram:SelectCriteriaToSeePreviewOfAffectedUsers")}
        </div>
      )}
    </div>
  );
}

interface RuleEditorPanelsProps {
  children: ReactNode;
  configurationAction?: ReactNode;
  configurationTitle: ReactNode;
  hasAnySelection: boolean;
  previewTitle: ReactNode;
  userPreviewBody: UserSearchBody;
}

export function RuleEditorPanels({
  children,
  configurationAction,
  configurationTitle,
  hasAnySelection,
  previewTitle,
  userPreviewBody,
}: RuleEditorPanelsProps): ReactElement {
  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(20rem,1fr)_minmax(0,1.5fr)]">
      <Card>
        <CardHeader>
          <CardTitle>{configurationTitle}</CardTitle>
          {configurationAction ? <CardAction>{configurationAction}</CardAction> : null}
        </CardHeader>
        <CardContent className="space-y-8">{children}</CardContent>
      </Card>

      <div className="relative min-h-96 xl:min-h-0">
        <Card className="absolute inset-0 overflow-hidden">
          <CardHeader>
            <CardTitle>{previewTitle}</CardTitle>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 overflow-hidden">
            <RuleAffectedUsersPreview hasAnySelection={hasAnySelection} userPreviewBody={userPreviewBody} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
